import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Users, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const bookingId = searchParams.get('booking_id');

    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (bookingId) {
      fetchBooking(bookingId);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    
    if (pollCount >= maxAttempts) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        fetchBooking(response.data.booking_id);
      } else {
        setPollCount(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error polling payment status:', error);
      setLoading(false);
    }
  };

  const fetchBooking = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-foreground/60">Confirming your booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading mb-4">Booking Not Found</h1>
          <Link to="/" className="btn-outline">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="booking-success-page">
      <div className="max-w-2xl mx-auto container-padding py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-glass p-8 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 mx-auto mb-6 bg-gold-500/20 rounded-full flex items-center justify-center"
          >
            <CheckCircle className="text-gold-500" size={40} />
          </motion.div>

          <h1 className="text-3xl md:text-4xl font-heading mb-2">
            Booking <span className="text-gold-500">Confirmed!</span>
          </h1>
          <p className="text-foreground/60 mb-8">
            Thank you for choosing Rudragarh. Your desert adventure awaits!
          </p>

          <div className="bg-void-subtle p-6 text-left space-y-4 mb-8">
            <div className="flex items-center justify-between pb-4 border-b border-gold-500/10">
              <span className="text-foreground/60">Booking ID</span>
              <span className="font-mono text-gold-500">{booking.id.slice(0, 8).toUpperCase()}</span>
            </div>

            <h3 className="font-heading text-xl">{booking.room_name}</h3>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="text-gold-500" size={16} />
                <div>
                  <p className="text-foreground/60">Check-in</p>
                  <p>{booking.check_in}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="text-gold-500" size={16} />
                <div>
                  <p className="text-foreground/60">Check-out</p>
                  <p>{booking.check_out}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="text-gold-500" size={16} />
                <div>
                  <p className="text-foreground/60">Guests</p>
                  <p>{booking.guests}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="text-gold-500" size={16} />
                <div>
                  <p className="text-foreground/60">Status</p>
                  <p className={booking.status === 'confirmed' ? 'text-green-500' : 'text-gold-500'}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gold-500/10 flex justify-between items-center">
              <span className="text-foreground/60">Total Amount</span>
              <span className="text-gold-500 font-heading text-2xl">₹{booking.total_amount.toLocaleString()}</span>
            </div>
          </div>

          <p className="text-foreground/60 text-sm mb-6">
            A confirmation email has been sent to <span className="text-gold-500">{booking.guest_email}</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-outline" data-testid="back-home-btn">
              Back to Home
            </Link>
            <Link to="/experiences" className="btn-primary" data-testid="explore-experiences-btn">
              Explore Experiences
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingSuccess;
